import React, { useState } from "react";
import type { AuctionInfo } from "@auction-eth/shared-types";
import { api } from "../services/api.js";
import { getCurrentUserId } from "../services/auth.js";

const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)", display: "block", marginBottom: 4 };
const fieldStyle: React.CSSProperties = { width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--bg)", color: "var(--ink)", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" };

/**
 * Format a stored UTC ISO string as a datetime-local input value (local time, no TZ).
 * Uses local-time getters instead of toISOString() so users in non-UTC timezones see
 * their clock time rather than UTC. Without this, EAT (UTC+3) users see times 3 h early.
 */
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

export function AuctionInfoEditor({
  caseId,
  initialData,
  onSaved,
  onCancel,
}: {
  caseId: string;
  initialData?: Partial<AuctionInfo>;
  onSaved: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const [form, setForm] = useState<Partial<AuctionInfo>>(initialData ?? {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update(key: keyof AuctionInfo, value: unknown): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await api.cases.updatePreAuctionSetup(caseId, form, getCurrentUserId());
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update auction info");
    } finally {
      setSaving(false);
    }
  }

  const secType = form.bid_security_type ?? "FIXED";

  return (
    <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 8, padding: 20 }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, color: "var(--ink)" }}>Edit Auction Parameters</h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--muted)" }}>
        These parameters will be locked once the auction is published to the platform.
      </p>
      {error && <div style={{ color: "#dc2626", marginBottom: 12, fontSize: 13 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div>
          <label style={lbl}>Starting Price (ETB)</label>
          <input type="number" min={0} style={fieldStyle} value={form.initial_price ?? ""}
            onChange={(e) => update("initial_price", Number(e.target.value))} />
        </div>
        <div>
          <label style={lbl}>Currency</label>
          <select style={fieldStyle} value={form.currency ?? "ETB"} onChange={(e) => update("currency", e.target.value)}>
            <option value="ETB">ETB — Ethiopian Birr</option>
            <option value="USD">USD — US Dollar</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Bidding Opens</label>
          <input type="datetime-local" style={fieldStyle}
            value={isoToLocalInput(form.start_time)}
            onChange={(e) => update("start_time", e.target.value ? new Date(e.target.value).toISOString() : null)} />
        </div>
        <div>
          <label style={lbl}>Bidding Closes</label>
          <input type="datetime-local" style={fieldStyle}
            value={isoToLocalInput(form.end_time)}
            onChange={(e) => update("end_time", e.target.value ? new Date(e.target.value).toISOString() : null)} />
        </div>
        <div>
          <label style={lbl}>Bid Security Type</label>
          <select style={fieldStyle}
            value={secType}
            onChange={(e) => update("bid_security_type", e.target.value as AuctionInfo["bid_security_type"])}>
            <option value="FIXED">Fixed Amount (CPO)</option>
            <option value="PERCENTAGE">Percentage of Starting Price</option>
          </select>
        </div>
        {secType === "FIXED" ? (
          <div>
            <label style={lbl}>Security Amount (ETB)</label>
            <input type="number" min={0} style={fieldStyle}
              value={form.min_amount_to_bid ?? ""}
              onChange={(e) => update("min_amount_to_bid", Number(e.target.value))} />
          </div>
        ) : (
          <div>
            <label style={lbl}>Security Percentage (%)</label>
            <input type="number" min={0} max={100} style={fieldStyle}
              value={form.percentage_bid_security ?? ""}
              onChange={(e) => update("percentage_bid_security", Number(e.target.value))} />
          </div>
        )}
        <div>
          <label style={lbl}>Min Bid Increment (ETB)</label>
          <input type="number" min={0} style={fieldStyle}
            value={form.min_increment ?? ""}
            onChange={(e) => update("min_increment", Number(e.target.value))} />
        </div>
        <div>
          <label style={lbl}>Max Bid Increment (ETB)</label>
          <input type="number" min={0} style={fieldStyle}
            value={form.max_increment ?? ""}
            onChange={(e) => update("max_increment", e.target.value === "" ? null : Number(e.target.value))} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
        <button onClick={onCancel}
          style={{ padding: "8px 16px", background: "transparent", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer" }}>
          Cancel
        </button>
        <button onClick={() => void save()} disabled={saving}
          style={{ padding: "8px 16px", background: "var(--brand)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
